import { Suspense, lazy, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useSesion } from '../lib/sesion';
import { RUTA_CLAVE_NUEVA } from '../lib/enlace-clave';
import { tituloNego } from '../lib/nego-permisos';
import {
  CAT, MARCA_COMO, RUTA_DE, destinoDe, modulosVisibles, paginasDe,
  permisosDeSesion, rutaDePathname, submodulosDe, type DefModulo,
} from '../lib/navegacion';
import Dhalia from './Dhalia';
import TiraTasas from './TiraTasas';
import Aviso from './Aviso';
import CambiarClave from './CambiarClave';
import Recorrido from './Recorrido';
import FraseMotivacional from './FraseMotivacional';
import logoGrupoDH from '../imagenes/kpi/logo-grupodh.png';

/* Perezosa a propósito: trae la cuenta de Viáticos (`viaticos-flujo`) y el
 * retrato de DHALIA, y solo hace falta justo después de iniciar sesión. Metida
 * en el paquete principal le sumaba 40 KB a TODAS las páginas del portal. */
const PendientesDhalia = lazy(() => import('./PendientesDhalia'));
/* El podio de promociones (db/286): también perezoso, solo cinco personas lo ven. */
const PodioPromos = lazy(() => import('./PodioPromos'));

/**
 * Marco de navegación — el `PMM` del monolito, con su estado de módulo.
 *
 * El porte anterior se había quedado con la mitad de dentro de `PMM.mark`:
 * resolvía el módulo SIEMPRE por «el primero de la lista que contiene el
 * submódulo de la página», sin guardar cuál había elegido la usuaria. Como los
 * submódulos están compartidos, eso hacía INALCANZABLES siete de los once
 * módulos. Medido: el botón de Cobranzas apuntaba a `/vendedores` —su primera
 * página en orden de `RUTAS`— y `/vendedores` resuelve a **C.E.I.**, que va
 * antes en la lista; así que Cobranzas no se marcaba, no abría su submenú y
 * llevaba al de C.E.I. Lo mismo Comercial, Facturación, Finanzas, Gerencia,
 * Inteligencia de Mercado y Trade Marketing. Resumen, Almacén y C.E.I.
 * funcionaban solo porque son los únicos que ganan ese «primero que lo tenga».
 *
 * Aquí el estado vive en la URL (`?mod=<id>`), que es el `PMM.cur` de una SPA:
 * sobrevive a un refresco, al botón de atrás y a un enlace compartido.
 * El modelo de datos (`MODS`/`CAT`) está en `lib/navegacion.ts`, porque DHALIA
 * también lo usa — igual que el v88, que se lleva una copia al bloque del bot.
 */
export function Marco({ children }: { children: ReactNode }) {
  const { sesion, salir, avisoPodio, avisoPendientes } = useSesion();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [plegados, setPlegados] = useState<Record<string, boolean>>({});
  /* Bajo `lg` el lateral no cabe: el mismo submenú se abre como panel
   * deslizante desde el botón ☰ de la cabecera. Sin esto, en una pantalla
   * chica siete de cada ocho páginas eran inalcanzables. */
  const [menuMovil, setMenuMovil] = useState(false);
  /* db/080 · Las dos piezas nuevas de la cabecera. El recorrido TAMBIÉN se
   * ofrece solo la primera vez (esa parte la decide `Recorrido.tsx` con lo que
   * dice la base); esto es el botón para repetirlo cuando el usuario quiera. */
  const [verRecorrido, setVerRecorrido] = useState(false);
  const [verClave, setVerClave] = useState(false);
  /* La invitación a poner clave propia se puede apartar POR SESIÓN: vuelve en la
   * próxima entrada y desaparece para siempre en cuanto la clave se cambia. Un
   * aviso que no se puede cerrar acaba siendo parte del decorado. */
  const [invitacionOculta, setInvitacionOculta] = useState(false);

  const permisos = useMemo(() => permisosDeSesion(sesion), [sesion]);
  const visibles = useMemo(() => modulosVisibles(permisos), [permisos]);

  const rutaActual = useMemo(() => rutaDePathname(pathname), [pathname]);
  const subActual = rutaActual?.submodulo ?? null;
  const paginaMarcada = rutaActual
    ? (MARCA_COMO[rutaActual.pagina] ?? rutaActual.pagina)
    : null;

  /**
   * `PMM.cur` + `PMM.mark()`: manda el módulo pedido en la URL, y solo se
   * reasigna cuando ese módulo NO contiene el submódulo de la página — que es
   * exactamente la condición del monolito.
   */
  const moduloActivo = useMemo<DefModulo | undefined>(() => {
    const pedido = visibles.find((m) => m.id === params.get('mod'));
    if (pedido && (!subActual || pedido.submodulos.includes(subActual))) return pedido;
    if (subActual) {
      const hallado = visibles.find((m) => m.submodulos.includes(subActual));
      if (hallado) return hallado;
    }
    return pedido ?? visibles[0];
  }, [params, subActual, visibles]);

  const submenu = useMemo(
    () => (moduloActivo ? submodulosDe(moduloActivo, permisos) : []),
    [moduloActivo, permisos],
  );

  /* El TÍTULO DE LA PESTAÑA avisa de la suplantación, y es el único indicador
   * que sobrevive al scroll, a un modal a pantalla completa y a mirar la barra
   * de pestañas sin volver a la página. La banda ámbar de abajo puede quedar
   * fuera de vista; esto no. Va antes del `return` de no-sesión para no alterar
   * el orden de hooks. */
  const suplantando = sesion?.suplantacion ? sesion.usuario.username : null;
  useEffect(() => {
    document.title = suplantando
      ? `⚠ COMO ${suplantando} · Portal Master · Grupo DH`
      : 'Portal Master · Grupo DH';
    return () => { document.title = 'Portal Master · Grupo DH'; };
  }, [suplantando]);

  /* Navegar cierra el panel; también al cambiar de módulo con la misma ruta. */
  useEffect(() => { setMenuMovil(false); }, [pathname, params]);

  if (!sesion) return <>{children}</>;

  /* db/106 · EL ENLACE DEL CORREO GANA AL MARCO, incluso con sesión abierta.
   *
   * Sin esta línea, quien tenga el portal abierto en el escritorio y pulse el
   * enlace que le llegó al correo vería la pantalla de reponer clave metida
   * dentro del marco —cabecera, lateral, DHALIA— o, peor, tapada por el portón
   * si además tenía el cambio pendiente. Y no es un caso raro: la sesión dura 8 h
   * y el correo se abre en la misma máquina.
   *
   * Va DESPUÉS de `!sesion` (que ya lo cubre sin sesión) y ANTES del portón: la
   * pantalla del enlace es otra salida legítima del portón, igual que
   * `/auth/password` lo es en el servidor. */
  if (pathname === RUTA_CLAVE_NUEVA) return <>{children}</>;

  /* EL PORTÓN DE CONTRASEÑA (2026-08-26). Va aquí y no en `rutas.tsx` porque
   * este es el único punto que tapa la aplicación ENTERA de una vez: la cabecera
   * pegajosa, la barra de módulos, el botón Salir, la banda de suplantación, el
   * lateral, `children` y DHALIA se pintan todos por debajo de esta línea. Una
   * ruta dentro de `<Routes>` saldría con el menú completo encima y se
   * esquivaría escribiendo otra URL.
   *
   * `!sesion.suplantacion` es la mitad importante de la condición. Con los 40
   * usuarios en `debe_cambiar = TRUE`, sin esa negación la administradora que
   * entra a una cuenta con la clave maestra —para ver qué ve esa persona, que es
   * para lo que se hizo— se encontraría el portón de un tercero y no podría
   * mirar nada. El servidor aplica la MISMA excepción en `requiereSesion()`, y el
   * endpoint de cambio rechaza de plano las sesiones suplantadas: la
   * administradora no puede pasar el portón por nadie.
   *
   * Y no es solo cosmético: con esto puesto y el usuario sin cambiar la clave, el
   * servidor ya devuelve 403 en los 51 endpoints. Si alguien quitara este
   * return, el portal se vería entero y vacío, no abierto. */
  if (sesion.usuario.debeCambiar && !sesion.suplantacion) return <CambiarClave porton />;

  const mod = moduloActivo?.id ?? '';
  /** Un submenú de un solo elemento no aporta nada: el v88 tampoco lo pinta. */
  const hayLateral = submenu.length > 1
    || (submenu.length === 1 && paginasDe(submenu[0]!, mod, permisos).length > 1);

  /* El MISMO submenú se pinta dos veces: lateral fijo en escritorio y panel
   * deslizante bajo `lg`. Una sola definición para que no diverjan. */
  const menuLateral = (
    <>
      <p className="px-3 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[0.16em] opacity-40">
        {moduloActivo?.nombre}
      </p>
      <div className="space-y-1">
        {submenu.map((sub) => {
          const c = CAT[sub]!;
          const pgs = paginasDe(sub, mod, permisos);
          /* Submódulo de una sola página visible: elemento suelto, como
           * el `c.pg` del v88. No hay grupo que desplegar. */
          if (!c.kids || pgs.length === 1) {
            return (
              <Elemento
                key={sub}
                pagina={pgs[0]!}
                /* Para las cuentas de Trade la sección es «Negociaciones Trade»
                 * (2026-10-29): ver `tituloNego` en `lib/nego-permisos.ts`. */
                titulo={c.kids ? RUTA_DE[pgs[0]!]!.titulo
                  : sub === 'nego' ? tituloNego(sesion.roles) : c.t}
                mod={mod}
                activa={paginaMarcada === pgs[0]}
              />
            );
          }
          /* Grupo desplegable. Abierto cuando la página en curso está
           * dentro, igual que `PMM.mark` hace `g.classList.add('open')`.
           * `plegados` guarda solo lo que la usuaria ha tocado a mano. */
          const contiene = sub === subActual;
          const abierto = plegados[sub] ?? contiene;
          return (
            <div key={sub}>
              <button
                type="button"
                onClick={() => setPlegados((p) => ({ ...p, [sub]: !abierto }))}
                aria-expanded={abierto}
                className={
                  'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition '
                  + (contiene ? 'font-semibold' : 'hover:bg-suave/60')
                }
              >
                {c.t}
                <svg
                  viewBox="0 0 24 24"
                  className={'ml-auto h-3 w-3 shrink-0 opacity-55 transition-transform '
                    + (abierto ? 'rotate-180' : '')}
                  fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {abierto && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-borde pl-2">
                  {pgs.map((pg) => (
                    <Elemento
                      key={pg}
                      pagina={pg}
                      titulo={RUTA_DE[pg]!.titulo}
                      mod={mod}
                      activa={paginaMarcada === pg}
                      hijo
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-fondo text-texto">
      {/* Cabecera rediseñada (2026-08-21, pedido de la usuaria): logo centrado
        * arriba, el menú de módulos en una barra tipo píldora debajo, y bajo el
        * menú la marquesina LED de tasas. YA NO ES STICKY: con el logo mide
        * ~130 px y fijarla se comería un cuarto de un teléfono en cada página.
        * Consecuencia medida en este archivo: el lateral ancla en `top-0` y el
        * aviso de suplantación se hace sticky POR SU CUENTA, más abajo. */}
      <header className="border-b border-borde bg-panel">
        {/* El relleno de la derecha (`pr-*`) reserva sitio para el grupo de
          * botones, que va posicionado en absoluto y por tanto no empuja nada:
          * sin él, con tres botones el logo centrado se les mete por debajo en un
          * teléfono. Medido a 375 px de ancho. */}
        <div className="relative flex items-center justify-center pb-1 pl-12 pr-[7.5rem]
                        pt-3 sm:pl-14 sm:pr-[9rem] lg:pr-[15rem]">
          {hayLateral && (
            <button
              type="button"
              onClick={() => setMenuMovil(true)}
              aria-label="Abrir menú de páginas"
              data-recorrido="menu-movil"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-lg p-2
                         hover:bg-suave lg:hidden"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {/* En tema oscuro el «GRUPO DH» del PNG es azul marino y desaparece:
            * invert + hue-rotate voltea la luminosidad conservando el magenta
            * de los pétalos — la «versión negativa» del logo, sin otro asset. */}
          <img
            src={logoGrupoDH}
            alt="Grupo DH — Inspirados por ti"
            data-recorrido="marca"
            className="h-10 w-auto max-w-[52vw] object-contain sm:h-12
                       dark:invert dark:hue-rotate-180"
          />

          {/*
            Los tres botones de la cuenta, juntos y en este orden. «Salir» se
            queda EL ÚLTIMO a la derecha —donde lleva estando desde el rediseño
            del 2026-08-21— y los dos nuevos entran a su izquierda: la usuaria
            pidió el del recorrido «al lado de salir» (2026-08-26).

            Bajo `lg` quedan solo los iconos: con los rótulos, los tres botones
            más el nombre del usuario no caben en un teléfono y empujaban el logo.
            El `title` y el `aria-label` conservan el texto en los dos casos.
          */}
          <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5
                          sm:right-2">
            {/* db/080 · El candado que pidió la usuaria: este botón NO existe
              * mientras el usuario siga con la clave que se le entregó. */}
            {sesion.usuario.clavePersonalizada && (
              <button
                type="button"
                onClick={() => setVerRecorrido(true)}
                title="Ver el recorrido guiado del portal"
                aria-label="Ver el recorrido guiado del portal"
                data-recorrido="boton-recorrido"
                className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs
                           font-semibold hover:bg-suave"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                  strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M10 8.5l6 3.5-6 3.5z" />
                </svg>
                <span className="hidden lg:inline">Recorrido</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setVerClave(true)}
              title="Cambiar mi contraseña"
              aria-label="Cambiar mi contraseña"
              data-recorrido="clave"
              className={
                'flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold '
                + 'hover:bg-suave '
                /* Mientras la clave sea la temporal, el botón se señala: es el
                 * único camino para desbloquear el recorrido. */
                + (sesion.usuario.clavePersonalizada ? '' : 'ring-1 ring-magenta')
              }
            >
              {/* Una LLAVE: anillo arriba a la derecha, caña hacia abajo a la
                * izquierda y dos dientes. La primera versión era un círculo con
                * una flecha diagonal y se leía como el símbolo de Marte (♂):
                * cazado en la captura de verificación del 2026-08-26. */}
              <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true">
                <circle cx="15.5" cy="8.5" r="3.5" />
                <path d="M13 11L4.5 19.5M7 17l2 2M9.5 14.5l2 2" />
              </svg>
              <span className="hidden lg:inline">Mi clave</span>
            </button>

            <button
              onClick={() => void salir()}
              data-recorrido="salir"
              className="rounded-lg px-2 py-1.5 text-xs font-semibold hover:bg-suave sm:px-3"
            >
              Salir<span className="hidden xl:inline"> · {sesion.usuario.nombre}</span>
            </button>
          </div>
        </div>

        {/* Menú píldora, centrado. `w-max mx-auto` lo centra cuando cabe y el
          * envoltorio `overflow-x-auto` lo deja desplazable cuando no (MASTER
          * ve 13 módulos: en un teléfono no caben, y `flex-1` los aplastaba).
          * OJO: fichas SÓLIDAS (`bg-suave`), nada de `bg-magenta/10` — con los
          * colores definidos como `var()` a secas, Tailwind v3 DESCARTA los
          * modificadores de opacidad sin avisar y la clase no existe. */}
        <div className="overflow-x-auto px-3 pb-3 pt-1">
          <nav
            data-recorrido="modulos"
            className="mx-auto flex w-max items-center gap-1 rounded-full border
                       border-borde bg-suave p-1"
          >
            {visibles.map((m) => {
              const destino = destinoDe(m, permisos);
              if (!destino) return null;
              const activo = m.id === moduloActivo?.id;
              return (
                <Link
                  key={m.id}
                  to={{ pathname: destino, search: `?mod=${m.id}` }}
                  {...(activo ? { 'aria-current': 'page' as const } : {})}
                  /* El anclaje del recorrido: un paso por módulo, y el selector
                   * se DERIVA de este mismo id en `lib/recorrido.ts`. */
                  data-recorrido-mod={m.id}
                  className={
                    'whitespace-nowrap rounded-full px-3.5 py-1.5 text-center text-[12px] '
                    + 'font-semibold leading-tight transition '
                    + (activo
                      ? 'pastilla-activa shadow'
                      : 'hover:bg-panel')
                  }
                >
                  {m.nombre}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      {/*
        db/080 · La invitación a poner clave propia. Solo la ve quien sigue con la
        que se le entregó, y desaparece para siempre en cuanto la cambia.

        No es un muro: son 40 cuentas en uso diario y varias son compartidas, así
        que obligar en el login dejaría a todo el mundo fuera el mismo día (la
        migración lo declara). Se invita, se explica qué se gana —el recorrido— y
        se puede apartar hasta la próxima entrada.

        En una sesión SUPLANTADA no se pinta: el servidor no dejaría cambiar la
        clave de otro (`auth-password.mts`), así que ofrecerlo sería mandar a la
        administradora a un 403.
      */}
      {!sesion.usuario.clavePersonalizada && !sesion.suplantacion && !invitacionOculta && (
        <div className="px-3 pt-2">
          <Aviso nivel="mid" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <span className="text-center">
              Sigues usando la contraseña que te entregamos. Pon la tuya y se activa el{' '}
              <strong>recorrido guiado</strong> del portal.
            </span>
            <button
              type="button"
              onClick={() => setVerClave(true)}
              className="rounded-lg border border-current px-2 py-0.5 font-semibold
                         hover:bg-amber-500/20"
            >
              Cambiar mi clave
            </button>
            <button
              type="button"
              onClick={() => setInvitacionOculta(true)}
              aria-label="Ocultar este aviso hasta la próxima entrada"
              className="rounded-lg px-1.5 opacity-70 hover:opacity-100"
            >
              ✕
            </button>
          </Aviso>
        </div>
      )}

      {/*
        Aviso de suplantación. La cabecera ya no es sticky, así que el aviso se
        fija SOLO: si fuera parte del flujo normal se iría de la pantalla con el
        primer scroll —y todas las páginas de datos son más altas que un
        viewport—, dejando como único rótulo el «Salir · {nombre}», que pinta el
        nombre del suplantado y es indistinguible de una sesión propia. Aquí
        queda pegado arriba siempre. (Un modal `z-50` sí lo tapa; el título de
        la pestaña, que se fija más arriba, es el respaldo para ese caso.)

        No es decoración: con la clave maestra el portal se ve idéntico al del
        usuario, así que sin esto no hay forma de saber en qué cuenta estás, y un
        descuido escribe algo que quedará firmado por el asesor.
      */}
      {sesion.suplantacion && (
        <div
          role="alert"
          className="sticky top-0 z-30 flex flex-wrap items-center justify-center gap-x-3
                     gap-y-1 border-b border-warn/50 bg-warn/20 px-4 py-2 text-center
                     text-xs font-medium text-warn backdrop-blur"
        >
          <span>
            ⚠ Estás viendo el portal <strong>como {sesion.usuario.username}</strong>
            {' '}({sesion.usuario.nombre}) — entraste con la clave maestra de{' '}
            <strong>{sesion.suplantacion.nombre}</strong>. La entrada quedó registrada.
          </span>
          <button
            onClick={() => void salir()}
            className="rounded-lg border border-warn/60 px-2 py-0.5 font-semibold hover:bg-warn/25"
          >
            Salir de esta cuenta
          </button>
        </div>
      )}

      {/* Tasas del día como marquesina LED (2026-08-21), justo bajo el menú.
        * Sigue en flujo normal: se va con el scroll y no le roba altura fija a
        * ninguna página. El motivo completo, en la cabecera de TiraTasas.tsx.
        *
        * El envoltorio es el anclaje del recorrido y no cambia el diseño: si
        * `TiraTasas` no pinta nada —no hay tasas del día—, el `div` queda con 0
        * de alto y el paso se salta solo, porque el recorrido mide el rectángulo
        * y no la existencia (`Recorrido.tsx`, decisión 2). */}
      <div data-recorrido="tasas">
        <TiraTasas />
      </div>

      <div className="flex">
        {/* `top-0`/`h-screen` porque la cabecera ya no es sticky: al hacer
          * scroll el lateral queda pegado al borde superior del viewport. */}
        {hayLateral && (
          <aside
            data-recorrido="lateral"
            className="sticky top-0 hidden h-screen w-[272px] shrink-0
                       overflow-y-auto border-r border-borde bg-panel p-3 lg:block"
          >
            {menuLateral}
          </aside>
        )}
        <main className="min-w-0 flex-1 p-3 sm:p-4">{children}</main>
      </div>

      {/* Panel deslizante bajo `lg`. Tocar el fondo o navegar lo cierra; el
        * cierre por navegación lo hace el efecto sobre pathname/params, y el
        * onClick delegado cubre el caso de pulsar la página ya activa. */}
      {hayLateral && menuMovil && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMenuMovil(false)}
          />
          <aside
            className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] overflow-y-auto
                       bg-panel p-3 shadow-xl"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('a')) setMenuMovil(false);
            }}
          >
            {menuLateral}
          </aside>
        </div>
      )}

      <Dhalia />

      {/* db/080 · Las dos piezas nuevas. Van AQUÍ, en el marco, y no en cada
        * página: el recorrido señala la cabecera y el menú, que son del marco, y
        * la clave no pertenece a ninguna sección. */}
      <CambiarClave abierto={verClave} onCerrar={() => setVerClave(false)} />
      <Recorrido pedido={verRecorrido} onCerrar={() => setVerRecorrido(false)} />

      {/* 2026-10-26 · La frase motivacional de cada inicio de sesión. Va aquí,
        * DESPUÉS del portón: quien tenga que cambiar la clave la ve al cruzarlo. */}
      <FraseMotivacional />
      {/* 2026-11-04 (db/286) · Entre la frase y los pendientes, el podio de
        * promociones con confeti: solo a quien está en él, y una sola vez. */}
      {avisoPodio && (
        <Suspense fallback={null}>
          <PodioPromos />
        </Suspense>
      )}
      {/* Y detrás de la frase, los pendientes de DHALIA: ventana aparte, como eligió
        * la usuaria (2026-10-26). Solo sale si hay algo que hacer. */}
      {avisoPendientes && (
        <Suspense fallback={null}>
          <PendientesDhalia />
        </Suspense>
      )}
    </div>
  );
}

/**
 * Un elemento del submenú. **A propósito es `Link` y no `NavLink`.** El
 * `isActive` de `NavLink` marca por PREFIJO de ruta y además ignora la query,
 * así que producía los dos síntomas que la usuaria describió: `/clientes` salía
 * activo a la vez que `/clientes/maestra` —«dos seleccionados al mismo tiempo»—
 * y los cuatro botones que apuntan a `/vendedores` se anunciaban activos los
 * cuatro. Aquí lo activo es una decisión de UNA sola página (`paginaMarcada`),
 * calculada arriba, y la marca visual y el `aria-current` salen de ella.
 */
function Elemento({ pagina, titulo, mod, activa, hijo }: {
  pagina: string; titulo: string; mod: string; activa: boolean; hijo?: boolean;
}) {
  return (
    <Link
      to={{ pathname: RUTA_DE[pagina]!.ruta, search: `?mod=${mod}` }}
      {...(activa ? { 'aria-current': 'page' as const } : {})}
      className={
        'flex items-center gap-2 rounded-lg px-3 py-2 transition '
        + (hijo ? 'text-[13px] ' : 'text-sm ')
        + (activa
          ? 'pastilla-activa font-semibold shadow'
          : 'hover:bg-suave/60')
      }
    >
      {titulo}
    </Link>
  );
}
